import LocalStorageNames from "@/constants/LocalStorageNames";
import { UserUiSettings } from "@/models/UserUiSettingsAddInData";

/**
 * A browser-local copy of one scope's settings.
 *
 * The stored add-in record is the real persistence; this copy exists only so a page can render
 * the settings the user actually has instead of flashing its defaults for the length of a
 * network round-trip — which for a date range also means firing the whole page's queries twice.
 *
 * Keyed by user and database rather than by `activeSessionId`: that one is only ever set by a
 * standalone login, so inside MyGeotab it stays null and there would be no local copy at all —
 * which is the mode that matters. The credentials Geotab hands over are available on the first
 * render, which is what makes the copy worth having.
 *
 * Several Geotab users share one browser profile, so an unscoped key would leak one user's
 * settings to the next login.
 */
export function settingsMirrorKey(
  username: string | undefined,
  database: string | undefined,
  scope: string,
): string | null {
  if (!username || !database) {
    return null;
  }
  return `${LocalStorageNames.userUiSettingsPrefix}${username} (${database})_${scope}`;
}

export function readSettingsMirror<TSettings extends UserUiSettings>(
  key: string | null,
): Partial<TSettings> | null {
  if (key === null) {
    return null;
  }

  try {
    const raw = localStorage.getItem(key);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Partial<TSettings>;
  } catch {
    // Unreadable or unparsable: fall back to the stored record.
    return null;
  }
}

export function writeSettingsMirror<TSettings extends UserUiSettings>(
  key: string | null,
  settings: Partial<TSettings>,
): void {
  if (key === null) {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    // A full or blocked storage must not break the page; the record is still written.
  }
}
